<script setup lang="ts">

import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import { usePermissions } from "@/composables/usePermissions";
import useSuperAdmin from "@/components/superadmin/useSuperAdmin";
import { useCompanyStore } from "@/stores/company";
import type { MenuItem } from "primevue/menuitem";
import { hasAnyRole } from "@/utils/userRoleHelper";
import {  SWITCH_APP_ROLES } from "@/utils/constants";

const { t } = useI18n();
const user = useUserStore()
const auth = useAuthStore()
const company = useCompanyStore()
const { safeUserRoles, safeUserIsReadOnly } = usePermissions();
const { showCompanySelectionModal } = useSuperAdmin();

const settingItems = ref<MenuItem[]>([
  {
    label: t("header.viewProfile"),
    url: 'user-information',
    icon: 'ri-user-line'
  },
  {
    label: "Language",
    icon: 'ri-translate-2',
    items: [
      {
        label: 'English',
        command: () => changeLanguage('en')
      },
      {
        label: 'Español',
        command: () => changeLanguage('es')
      }
    ]
  },
  {
    label: t("header.signOut"),
    command: () => { logout()},
    icon: 'ri-login-box-line'
  }
])

const companyName = computed(() => {
  if (!company.company.companyName) return '';

  const words = company.company.companyName.split(" ");
  const nameToDisplay = words.length > 1 ? words[0] : company.company.companyName;
  const formattedName = nameToDisplay.charAt(0).toUpperCase() + nameToDisplay.slice(1).toLowerCase();
  return formattedName.slice(0, 10);
});

const items = computed<MenuItem[]>(() => {
  // Guard clause for undefined userRoles
  if (!safeUserRoles.value || !Array.isArray(safeUserRoles.value) || safeUserRoles.value.length === 0) {
    return settingItems.value;
  }

  // READ_ONLY kullanıcılar sadece profil görebilir, company ayarları göremez
  const isAdmin = !safeUserIsReadOnly.value;
  const updatedItems = [...settingItems.value];

  if (isAdmin) {
    const companyItem: MenuItem = {
      label: t("header.viewCompany"),
      url: 'company-information',
      icon: 'ri-building-4-line'
    };

    updatedItems.unshift(companyItem);

    const superAdmin = hasAnyRole(safeUserRoles.value, SWITCH_APP_ROLES);

    if (superAdmin) {
      updatedItems.unshift({
        label: 'Change Company',
        command: () => showCompanySelectionModal(),
        icon: 'ri-swap-box-line'
      } as MenuItem);
      return updatedItems.filter(item => item.url !== 'user-information');
    }

    return updatedItems;
  }

  return settingItems.value;
});

const { logout } = auth;
const menu = ref();

const toggle = (event: any) => {
  menu.value.toggle(event);
};
const { locale: currentLocale } = useI18n();

const changeLanguage = (locale: string) => {
  currentLocale.value = locale;
  localStorage.setItem("locale", locale);
  window.location.reload();
};
</script>

<template>
  <Button variant="outlined"
          icon="ri-settings-2-line"
          iconPos="right"
          :label="companyName"
          @click="toggle"
          severity="contrast"
          aria-haspopup="true"
          aria-controls="overlay_menu"
  />
  <TieredMenu ref="menu" id="overlay_menu" :model="items" :popup="true" severity="contrast"  />
</template>
